import { useEffect, useCallback, useMemo } from "react";
import { Formik, FormikProps, Form, FormikHelpers } from "formik";
import { Grid, Button, Theme, useTheme, styled, alpha } from "@mui/material";
import { IconButton } from "src/components/IconButton/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import classNames from "classnames";
// Redux Imports
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
// Formik Fields
import { FormikSelectField, FormikTextField } from "src/components/FormikComponents/FormikWrappers";
import * as Yup from "yup";
import { IBooking, IBookingAgency, IBookingCrew } from "@vmosolutions/oc-interfaces";
import { BookingFlightDropTarget } from "../BookingFlightDropTarget";

import { getCrew } from "src/redux/actions/CrewSchedule/getCrew";
import { gotCrewAction } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { getAgencies, gotAgencies } from "src/redux/actions/CrewTravel/CrewTravel.Actions";

// API calls
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
interface IBookingFormProps {
  isNew: boolean;
  booking: IBooking;
  closeForm: () => void;
  mosaicPane?: HTMLElement;
}

const OCBookingForm = styled("div")(({ theme }) => ({
  position: "relative",
  width: "100%",
  padding: "20px 5px 0px 5px",
  backgroundColor: theme.palette.background.paper,
  "& .close_bookingform_btn": {
    position: "absolute",
    padding: 0,
    borderRadius: 3,
    right: 5,
    top: 5,
  },
  "& .formik_field_container": {
    "& .formik_field": {
      maxWidth: 350,
    },
    "& p.required_fields_label": {
      margin: 0,
      fontWeight: 300,
      fontSize: 10,
      textTransform: "uppercase",
      color: theme.custom.forms.labels,
    },
    "& .required_fields": {
      backgroundColor: alpha(theme.custom.forms.labels, 0.2),
      margin: "2px 0px",
      "& .agency_selectfield, & .crew_selectfield": {
        width: 250,
        "& label": { width: 50 },
      },
    },

    // STYLE FIELDS (includes individual field settings)
    "& .field": {
      width: "auto",
      minWidth: 238,
      "&.textfield": {
        "&.multiline_short": {
          width: 350,
          "& .input_control": {
            "& label": { width: "auto" },
          },
        },
        "&.multiline_long": {
          width: "100%",
          maxWidth: 540,
          "& .input_control": {
            "& label": { width: 150 },
          },
        },
        "&.name, &.email": { width: 300 },
        "&.description": { width: 500 },
        "&.rate, &.priority, &.taxrate, &.traveltime, &.minstay": {
          width: "auto",
          minWidth: "auto",
          "& label": { width: "auto" },
          "& .input-base": {
            width: 85,
            height: 26,
          },
        },
      },
      "&.selectfield": {
        "&.ratecurrency, &.icao": {
          width: 200,
          minWidth: 200,
          "& label": { width: "auto" },
        },
      },
    },

    "& .form_actions": {
      margin: 10,
      "& button": {
        margin: "0px 2px",
        fontSize: 11,
        lineHeight: "12px",
      },
    },
  },

  "& .booking_drop": {
    width: "100%",
    "& .booking_flight": { cursor: "pointer" },
  },
}));

// New Non Flying Assignment default values.
export const initialValues: IBooking = {
  locator: undefined,
  agency: undefined,
  bookingFlights: [],
  crew: [],
  custom: undefined,
  content: [],
  createdBy: undefined,
  createdAt: undefined,
};

const BookingFieldsSchema = Yup.object().shape({
  locator: Yup.string().required("Locator is required"),
  agency: Yup.object().required("Agency is required"),
  bookingFlights: Yup.array().required().min(1, "Booking Flight is required"),
  crew: Yup.array().required().min(1, "At least one crew member must be assigned."),
});

export type IBookingForm = FormikProps<Partial<IBooking>>;

export const BookingForm = (props: IBookingFormProps): JSX.Element => {
  const { isNew, mosaicPane, booking, closeForm } = props;
  const theme: Theme = useTheme();
  const dispatch = useDispatch();

  // Retrieve options to populate selection menus
  const agencies = useSelector((state: IRootState) => state.crewTravel.agencies);
  const crewMembers: ICrewMember[] = useSelector((state: IRootState) => state.crewSchedule.crewMembers);

  //TODO Investigate why agency property is being returned back from backend in array format --CC
  // Remove this once the issue has been resolved on the backend data.
  const fixedBookingValues = useMemo(() => {
    const bookingValues = { ...initialValues, ...booking };
    if (!bookingValues.createdBy && bookingValues._id) bookingValues._id = "_temp" + bookingValues._id;
    if (Array.isArray(booking?.agency) && booking?.agency?.length > 0) {
      return { ...bookingValues, agency: booking.agency[0] };
    } else if (Array.isArray(booking?.agency) && booking?.agency?.length === 0) {
      return { ...bookingValues, agency: null };
    }
    return bookingValues;
  }, [booking]);

  const getAgenciesAction = useCallback(async () => {
    const results = await getAgencies();
    if (results?.length > 0) {
      dispatch(gotAgencies(results));
    }
  }, [dispatch]);

  const getCrewAction = useCallback(async () => {
    const fetchedCrew: ICrewMember[] = await getCrew();
    dispatch(gotCrewAction(fetchedCrew));
  }, [dispatch]);

  // If Agencies are not loaded, retrieve them.
  useEffect(() => {
    if (agencies.length === 0) getAgenciesAction();
  }, [agencies, getAgenciesAction]);

  // If Crew are not loaded, retrieve them.
  useEffect(() => {
    if (crewMembers.length === 0) getCrewAction();
  }, [crewMembers, getCrewAction]);

  const crewOptions: IBookingCrew[] = useMemo(() => {
    return crewMembers?.length > 0
      ? crewMembers.map((crewMember: ICrewMember) => {
          const { _id, firstName, lastName, employeeId } = crewMember;
          return {
            crewInfo: {
              _id: _id,
              firstName: firstName,
              lastName: lastName,
            },
            employeeId: employeeId,
          };
        })
      : [];
  }, [crewMembers]);

  // If form values
  const handleSubmit = useCallback(
    async (values: IBooking, helpers: FormikHelpers<IBooking>): Promise<void> => {
      const { setStatus, resetForm, setSubmitting } = helpers;
      const isNew = !values._id || values._id.includes("_temp");
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: `${!isNew ? "Updating" : "Creating New"} Booking...`,
        successMessage: `Booking Successfully ${!isNew ? "Updated" : "Created"}.`,
        errorMessage: `Error: Unable to ${!isNew ? "Update" : "Create"} Booking`,
      };
      const response = isNew
        ? await AmplifyAPIWrapper.Post<IBooking>("/booking", values, notifications)
        : await AmplifyAPIWrapper.Put<IBooking>(`/booking/${values._id}`, values, notifications);

      if (response?._id) {
        resetForm({ values: response });
        setStatus({ success: true });
      } else {
        setStatus({ success: false });
      }
      setSubmitting(false);
    },
    [dispatch]
  );

  return (
    <OCBookingForm className={classNames(isNew && "new")} onClick={(event: any) => event.stopPropagation()}>
      <IconButton className="close_bookingform_btn" onClick={() => closeForm()} size="large">
        <CloseIcon />
      </IconButton>
      <Formik
        initialValues={fixedBookingValues}
        enableReinitialize={true}
        //@ts-ignore
        onSubmit={handleSubmit}
        validationSchema={BookingFieldsSchema}
      >
        {(formProps: IBookingForm) => {
          const { isValid, dirty, isSubmitting } = formProps;
          return (
            <Form>
              <Grid container className="formik_field_container" direction="column">
                <Grid item style={{ display: "contents" }}>
                  <Grid container direction="row" wrap="wrap">
                    <Grid item style={{ margin: 5, flexGrow: 1 }}>
                      <p className="required_fields_label">required fields</p>
                      <Grid container className="required_fields" direction="row">
                        <Grid item>
                          <FormikTextField
                            className="locator_textfield"
                            label="Locator"
                            name="locator"
                            parse={(value: string) => value?.trim()?.toUpperCase()}
                          />
                        </Grid>
                        <Grid item>
                          <FormikSelectField
                            className="agency_selectfield"
                            label="Agency"
                            name="agency"
                            options={agencies.sort((a, b) => (b.title > a.title ? -1 : 1))}
                            getOptionLabel={(agency: IBookingAgency) => agency.title}
                            getOptionValue={(agency: IBookingAgency) => agency._id}
                            isSearchable={true}
                            menuPortalTarget={mosaicPane}
                            menuPosition={"fixed"}
                            placeholder="Select booking agency..."
                            openMenuOnFocus={false}
                            isClearable={false}
                          />
                        </Grid>
                        <Grid item>
                          <FormikSelectField
                            className="crew_selectfield"
                            label="Crew"
                            name="crew"
                            options={crewOptions.sort((a, b) => {
                              return a.crewInfo.lastName.toLowerCase().localeCompare(b.crewInfo.lastName.toLowerCase());
                            })}
                            getOptionLabel={(crew: IBookingCrew) =>
                              `${crew?.crewInfo?.lastName}, ${crew?.crewInfo?.firstName} ${
                                crew?.employeeId ? `(${crew.employeeId})` : ""
                              }`
                            }
                            getOptionValue={(crew) => crew?.crewInfo?._id}
                            isSearchable={true}
                            menuPortalTarget={mosaicPane}
                            menuPosition={"fixed"}
                            placeholder="Select crew on booking..."
                            isMulti={true}
                            openMenuOnFocus={false}
                            isClearable={false}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item style={{ margin: 5, flexGrow: 1 }}>
                      <BookingFlightDropTarget />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item>
                  <div className="form_actions">
                    <Button type="reset" variant="contained" color="secondary" disabled={!dirty} style={{ margin: 5 }}>
                      Reset
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={!dirty || !isValid || isSubmitting}
                      style={{ margin: 5 }}
                    >
                      {isNew ? "Create" : "Update Booking"}
                    </Button>
                  </div>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </OCBookingForm>
  );
};
